import * as React from 'react';
import { type RecordOut } from '@/api/client';
import { formatDateBulletTime } from '@/shared/lib/format';
import type { LocalAudioRecord } from '../../types';
import { buildAudioUrl } from './utils';
import { AudioPlayer, type AudioPlayerHandle } from '@/features/recording';
import { RecordSummarySection } from './RecordSummarySection';
import { useAudioRecordMutations } from '../../hooks/useAudioRecordMutations';

export interface AudioRecordsListProps {
  remoteRecords: RecordOut[];
  localRecords: LocalAudioRecord[];
  profileId: string;
  baseUrl: string;
  onRenameLocalRecord?: (args: { profileId: string; localId: string; name: string }) => void;
  onDeleteLocalRecord?: (args: { profileId: string; localId: string }) => void;
  onRetryUploadLocalRecord?: (args: { profileId: string; localId: string }) => void;
  /** Entry id (`local-…` or file id) to scroll into view once after mount. */
  scrollToRecordId?: string | null;
  registerTourTarget?: (id: string, el: HTMLElement | null) => void;
}

export function AudioRecordsList({
  remoteRecords,
  localRecords,
  profileId,
  baseUrl,
  onDeleteLocalRecord,
  onRetryUploadLocalRecord,
  onRenameLocalRecord,
  scrollToRecordId,
  registerTourTarget,
}: AudioRecordsListProps) {
  const { renameRecord, deleteRecord } = useAudioRecordMutations();
  const [deletedById, setDeletedById] = React.useState<Record<string, true>>({});
  const [renamedById, setRenamedById] = React.useState<Record<string, string>>({});
  const [isDeletingById, setIsDeletingById] = React.useState<Record<string, boolean>>({});
  const [isRenamingById, setIsRenamingById] = React.useState<Record<string, boolean>>({});
  const [activeAudioId, setActiveAudioId] = React.useState<string | null>(null);
  const playerRefs = React.useRef(new Map<string, AudioPlayerHandle>());
  const scrolledToIdRef = React.useRef<string | null>(null);

  const scrollTargetRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !scrollToRecordId) return;
      if (scrolledToIdRef.current === scrollToRecordId) return;
      scrolledToIdRef.current = scrollToRecordId;
      el.scrollIntoView({ block: "start" });
    },
    [scrollToRecordId],
  );

  const setPlayerRef = React.useCallback((id: string, handle: AudioPlayerHandle | null) => {
    if (handle) {
      playerRefs.current.set(id, handle);
    } else {
      playerRefs.current.delete(id);
    }
  }, []);

  const seekRecordTo = React.useCallback((recordId: string, seconds: number) => {
    setActiveAudioId(recordId);
    const player = playerRefs.current.get(recordId);
    if (!player) return;
    player.seekTo(seconds);
    player.play();
  }, []);

  const entries = React.useMemo(() => {
    const remotes = remoteRecords
      .map((recordItem) => {
        const fileId = recordItem.file_id;
        const url = buildAudioUrl({ fileId, baseUrl, profileId });
        return {
          id: fileId,
          name: recordItem.name,
          url,
          date: recordItem.date,
          isLocal: false as const,
          displayName: renamedById[fileId] ?? recordItem.name,
          copilotSessionId: recordItem.copilot_session_id ?? undefined,
        };
      })
      .filter((record) => Boolean(record.url));

    const remoteIds = new Set(remotes.map((r) => r.id));

    const locals = localRecords
      .filter((record) => !(record.file_id && remoteIds.has(record.file_id)))
      .map((record) => ({
        id: `local-${record.id}`,
        name: record.name,
        url: record.url,
        duration: record.duration,
        durationSec: record.durationSec,
        date: record.date,
        time: record.time,
        isUploading: record.isUploading,
        uploadError: record.uploadError,
        isLocal: true as const,
        displayName: renamedById[`local-${record.id}`] ?? record.name,
        copilotSessionId: record.copilotSessionId,
        copilotSummary: record.copilotSummary,
        isFinalizing: record.isFinalizing,
        summaryFailed: record.summaryFailed,
      }));

    return [...remotes, ...locals]
      .filter((r) => !deletedById[r.id]);
  }, [baseUrl, deletedById, localRecords, profileId, remoteRecords, renamedById]);

  if (entries.length === 0) return null;

  // Prefer the scrolled-to demo record; otherwise the newest local entry.
  const tourRecordId =
    scrollToRecordId && entries.some((e) => e.id === scrollToRecordId)
      ? scrollToRecordId
      : [...entries].reverse().find((e) => e.isLocal)?.id ?? null;

  return (
    <div className="flex flex-col gap-8 mb-8">
      {entries.map((record) => {
        const key = record.id;
        const isLocal = record.isLocal;
        const registerThisSummary = Boolean(
          registerTourTarget && tourRecordId && key === tourRecordId,
        );

        const onDelete = async () => {
          if (isLocal) {
            const localId = key.replace(/^local-/, '');
            onDeleteLocalRecord?.({ profileId, localId });
            return;
          }

          setIsDeletingById((prev) => ({ ...prev, [key]: true }));
          try {
            await deleteRecord({ profileId, fileId: key });
            setDeletedById((prev) => ({ ...prev, [key]: true }));
          } catch {
            // Global mutation toast.
          } finally {
            setIsDeletingById((prev) => ({ ...prev, [key]: false }));
          }
        };

        const onRename = async (newName: string) => {
          if (isLocal) {
            const localId = key.replace(/^local-/, '');
            onRenameLocalRecord?.({ profileId, localId, name: newName });
            setRenamedById((prev) => ({ ...prev, [key]: newName }));
            return;
          }

          setIsRenamingById((prev) => ({ ...prev, [key]: true }));
          try {
            await renameRecord({ profileId, fileId: key, name: newName });
            setRenamedById((prev) => ({ ...prev, [key]: newName }));
          } catch {
            // Global mutation toast.
          } finally {
            setIsRenamingById((prev) => ({ ...prev, [key]: false }));
          }
        };

        let displayDate = record.date;
        if (isLocal && 'time' in record && record.time) {
          displayDate = `${record.date} • ${record.time}`;
        } else if (!isLocal && record.date) {
          const dateObj = new Date(record.date);
          if (!isNaN(dateObj.getTime())) {
            displayDate = formatDateBulletTime(dateObj);
          }
        }

        const uploadError = 'uploadError' in record ? record.uploadError : undefined;

        const copilotSessionId = 'copilotSessionId' in record ? record.copilotSessionId : undefined;

        const isFinalizing = isLocal && 'isFinalizing' in record ? record.isFinalizing : false;
        const summaryFailed = isLocal && 'summaryFailed' in record ? Boolean(record.summaryFailed) : false;
        const initialSummary = isLocal && 'copilotSummary' in record ? record.copilotSummary : undefined;
        const showSummary = Boolean(copilotSessionId) || Boolean(isFinalizing);

        return (
          <div
            key={key}
            ref={key === scrollToRecordId ? scrollTargetRef : undefined}
            className="flex flex-col gap-2"
            data-audio-record-id={key}
          >
            <AudioPlayer
              ref={(handle) => setPlayerRef(key, handle)}
              url={record.url}
              durationSec={'durationSec' in record ? record.durationSec : undefined}
              title={record.displayName}
              date={displayDate}
              onDelete={onDelete}
              onRename={onRename}
              isUploading={'isUploading' in record ? record.isUploading : false}
              isDeleting={isDeletingById[key]}
              isRenaming={isRenamingById[key]}
              uploadError={uploadError}
              isActive={activeAudioId === key}
              onPlay={() => setActiveAudioId(key)}
              onRetryUpload={isLocal && uploadError ? () => {
                const localId = key.replace(/^local-/, '');
                onRetryUploadLocalRecord?.({ profileId, localId });
              } : undefined}
            />
            {showSummary && copilotSessionId && (
              <RecordSummarySection
                copilotSessionId={copilotSessionId}
                initialSummary={initialSummary}
                isFinalizing={isFinalizing}
                summaryFailed={summaryFailed}
                onSeekToTimecode={(seconds) => seekRecordTo(key, seconds)}
                registerTourTargets={registerThisSummary}
                registerTourTarget={
                  registerThisSummary ? registerTourTarget : undefined
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
